import React, { useState } from "react";

import { Close } from "@mui/icons-material";
import {
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import _ from "lodash";
import { SelectField } from "src/components/Forms/DefaultInputs";

import { Territory } from "@models/Territory";
import { useApiResource } from "@services/api";
import { cn } from "@utils/tailwind";

type SubStateSelectionProps = {
  regionalTerritories: Territory[];
  selection: string[];
  setSelection: (selection: string[]) => void;
};

const SubStateSelection: React.FC<SubStateSelectionProps> = ({
  selection,
  setSelection,
  regionalTerritories,
}) => {
  const [territoryIdFilter, setTerritoryIdFilter] = useState("");
  const { data, isFetching } = useApiResource("sub-states", {
    params: {
      filter: territoryIdFilter ? { territoryId: territoryIdFilter } : {},
    },
    staleTime: Infinity,
  });
  const subStateList = _.sortBy(data ?? [], "name");

  const allSubStateIds = subStateList.map((obj) => obj.id);

  const handleToggleSubState = (subStateId: string) => {
    setSelection(_.xor(selection, [subStateId]));
  };

  const handleSelectAllSubStates = () => {
    if (_.difference(allSubStateIds, selection).length === 0) {
      setSelection(_.difference(selection, allSubStateIds));
    } else {
      setSelection(_.union(selection, allSubStateIds));
    }
  };

  return (
    <>
      <ListItem className="sticky top-0 bg-white flex justify-between items-center gap-6 z-10">
        <SelectField
          label="Filter by Territory"
          options={regionalTerritories.map((terr) => ({
            label: terr.name,
            value: terr.id,
          }))}
          value={territoryIdFilter}
          onChange={(e) => setTerritoryIdFilter(e.target.value as string)}
          IconComponent={
            territoryIdFilter
              ? () => (
                  <IconButton
                    size="small"
                    onClick={() => setTerritoryIdFilter("")}
                    className="mr-2"
                  >
                    <Close className="text-xl" />
                  </IconButton>
                )
              : undefined
          }
          className="flex-1"
        />
        <button
          onClick={handleSelectAllSubStates}
          className="text-sm underline"
        >
          Select All
        </button>
      </ListItem>
      <List className={cn(isFetching && "opacity-50 pointer-events-none")}>
        {subStateList.map((substate) => (
          <ListItemButton
            dense
            key={substate.id}
            onClick={() => handleToggleSubState(substate.id)}
          >
            <ListItemIcon>
              <Checkbox
                checked={!!selection.includes(substate.id)}
                className="-my-2"
              />
            </ListItemIcon>
            <ListItemText
              primary={<div className="flex gap-2">{substate.name}</div>}
            />
          </ListItemButton>
        ))}
      </List>
    </>
  );
};

export default SubStateSelection;
